import React, { useState, useEffect, useMemo } from "react";
import { useGetTopics, useGetLessonRatings } from "../../../api/queries";
import Combobox from "../../../componentLibrary/Combobox";
import Card from "../../../componentLibrary/Card";
import Spinner from "../../../componentLibrary/Spinner";
import Button from "../../../componentLibrary/Button";
import { LessonRatingI, UserSegmentEnum } from "../../../types";
import HorizontalBarChart from "./reusable/HorizontalBarChart";

const LESSON_RATING_CHART_COLORS = [
  { bg: "rgba(236, 72, 153, 0.6)", border: "rgb(236, 72, 153)" },
  { bg: "rgba(59, 130, 246, 0.6)", border: "rgb(59, 130, 246)" },
  { bg: "rgba(16, 185, 129, 0.6)", border: "rgb(16, 185, 129)" },
  { bg: "rgba(245, 158, 11, 0.6)", border: "rgb(245, 158, 11)" },
  { bg: "rgba(139, 92, 246, 0.6)", border: "rgb(139, 92, 246)" },
] as const;

function roundRatingAverage(value: number): number {
  return Math.round(value * 10) / 10;
}

function buildRatingChartDatasets(lessonRatings: LessonRatingI[]): {
  allCategoriesWithOverall: string[];
  chartDatasets: {
    label: string;
    dataKey: string;
    backgroundColor: string;
    borderColor: string;
  }[];
} {
  const allCategories = new Set<string>();
  lessonRatings.forEach((rating) => {
    if (rating.subFeedbackAverages) {
      Object.keys(rating.subFeedbackAverages).forEach((key) =>
        allCategories.add(key),
      );
    }
  });
  const allCategoriesWithOverall = [
    "Overall Rating",
    ...Array.from(allCategories),
  ];

  const chartDatasets = allCategoriesWithOverall.map((category, idx) => {
    const baseColor =
      LESSON_RATING_CHART_COLORS[idx % LESSON_RATING_CHART_COLORS.length];
    return {
      label: category,
      dataKey: category,
      backgroundColor: baseColor.bg,
      borderColor: baseColor.border,
    };
  });

  return { allCategoriesWithOverall, chartDatasets };
}

/** Response-weighted averages across lessons; matches pooling all raw answers in scope. */
function buildWeightedAggregateRatingRow(
  lessonRatings: LessonRatingI[],
  allCategoriesWithOverall: string[],
): {
  _id: string;
  title: string;
  count?: number;
  overallCount?: number;
  [key: string]: string | number | undefined;
} {
  const totalCount = lessonRatings.reduce((sum, r) => sum + r.count, 0);
  const overallNumerator = lessonRatings.reduce(
    (sum, r) => sum + r.average * r.count,
    0,
  );
  const overallAverage =
    totalCount > 0 ? roundRatingAverage(overallNumerator / totalCount) : 0;

  const row: {
    _id: string;
    title: string;
    count?: number;
    overallCount?: number;
    [key: string]: string | number | undefined;
  } = {
    _id: "__all_lessons_aggregate__",
    title: "Average (all lessons in scope)",
    count: totalCount,
    overallCount: totalCount,
    "Overall Rating": overallAverage,
  };

  for (const category of allCategoriesWithOverall) {
    if (category === "Overall Rating") continue;

    let weightedSum = 0;
    let categoryTotalCount = 0;
    for (const r of lessonRatings) {
      const c = r.subFeedbackCounts?.[category] ?? 0;
      if (c > 0 && r.subFeedbackAverages?.[category] !== undefined) {
        weightedSum += r.subFeedbackAverages[category] * c;
        categoryTotalCount += c;
      }
    }
    row[category] =
      categoryTotalCount > 0
        ? roundRatingAverage(weightedSum / categoryTotalCount)
        : 0;
    row[`${category}_count`] = lessonRatings.reduce(
      (sum, r) => sum + (r.subFeedbackCounts?.[category] ?? 0),
      0,
    );
  }

  return row;
}

interface FeedbackAnalyticsWidgetProps {
  selectedSegments?: UserSegmentEnum[];
  startDate?: string;
  endDate?: string;
  onLessonSelect?: (lessonId: string, topicId: string) => void;
}

const FeedbackAnalyticsWidget = ({
  selectedSegments = [],
  startDate,
  endDate,
  onLessonSelect,
}: FeedbackAnalyticsWidgetProps) => {
  const [topicId, setTopicId] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("highest");
  const [selectedRatingType, setSelectedRatingType] =
    useState<string>("Overall Rating");
  const [selectedLessonId, setSelectedLessonId] = useState<string>("");

  const {
    data: topics,
    isError: isErrorTopics,
    isLoading: isLoadingTopics,
  } = useGetTopics();

  const {
    data: lessonRatings,
    isLoading: isLoadingRatings,
    isError: isErrorRatings,
  } = useGetLessonRatings(selectedSegments, topicId, startDate, endDate);

  // Get available rating type options dynamically
  const availableRatingTypes = useMemo(() => {
    const types = new Set<string>();
    types.add("Overall Rating");

    if (lessonRatings) {
      lessonRatings.forEach((rating) => {
        if (rating.subFeedbackAverages) {
          Object.keys(rating.subFeedbackAverages).forEach((key) =>
            types.add(key),
          );
        }
      });
    }

    return Array.from(types);
  }, [lessonRatings]);

  // Reset rating type if it's no longer available
  useEffect(() => {
    if (
      selectedRatingType &&
      !availableRatingTypes.includes(selectedRatingType)
    ) {
      setSelectedRatingType("Overall Rating");
    }
  }, [availableRatingTypes, selectedRatingType]);

  function getTopicComboboxValue() {
    if (!topics || !topicId) return { value: "", label: "All Topics" };
    const topicObj = topics.find((topic) => topic._id === topicId);
    return topicObj
      ? { value: topicObj._id, label: topicObj.title }
      : { value: "", label: "All Topics" };
  }

  function getLessonTitle(lessonId: string): string {
    if (!topics) return "Unknown Lesson";
    for (const topic of topics) {
      const lesson = topic.lessons.find((l) => l._id === lessonId);
      if (lesson) return lesson.title;
    }
    return "Unknown Lesson";
  }

  function getTopicTitleForLesson(lessonId: string): string {
    if (!topics) return "";
    for (const topic of topics) {
      const lesson = topic.lessons.find((l) => l._id === lessonId);
      if (lesson) return topic.title;
    }
    return "";
  }

  function getTopicIdForLesson(lessonId: string): string | null {
    if (!topics) return null;
    for (const topic of topics) {
      const lesson = topic.lessons.find((l) => l._id === lessonId);
      if (lesson) return topic._id;
    }
    return null;
  }

  // Sort ratings
  const sortedRatings = lessonRatings
    ? [...lessonRatings].sort((a, b) => b.average - a.average)
    : [];

  const lowestRatings = lessonRatings
    ? [...lessonRatings].sort((a, b) => a.average - b.average)
    : [];

  // Check if any filters are active (not at default values)
  const hasActiveFilters = topicId !== "";

  // Check if graph-specific filters are active
  const hasActiveGraphFilters =
    sortOrder !== "highest" || selectedRatingType !== "Overall Rating";

  const ratingChartMeta =
    lessonRatings && lessonRatings.length > 0
      ? buildRatingChartDatasets(lessonRatings)
      : null;
  const aggregateRatingRow =
    ratingChartMeta && lessonRatings && lessonRatings.length > 0
      ? buildWeightedAggregateRatingRow(
          lessonRatings,
          ratingChartMeta.allCategoriesWithOverall,
        )
      : null;

  if (isLoadingTopics) return <Spinner className="mx-auto" />;
  if (isErrorTopics || !topics) return <div>Error loading topics</div>;

  return (
    <Card className="space-y-4">
      <h3 className="text-center m-0 mb-3 text-gray-700 text-lg underline">
        User Feedback - Lessons
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Topic
          </label>
          <Combobox
            options={[
              { value: "", label: "All Topics" },
              ...(topics?.map((topic) => ({
                value: topic._id,
                label: topic.title,
              })) || []),
            ]}
            onChange={(value) => setTopicId(value.value)}
            value={getTopicComboboxValue()}
            placeholder="All Topics"
          />
        </div>
        <div className="flex items-end">
          <Button
            variant={hasActiveFilters ? "dark" : "secondary"}
            size="md"
            onClick={() => {
              setTopicId("");
            }}
            className="w-full"
            disabled={!hasActiveFilters}
          >
            Clear Filter
          </Button>
        </div>
      </div>

      {isLoadingRatings ? (
        <div className="flex justify-center py-8">
          <Spinner className="mx-auto" />
        </div>
      ) : isErrorRatings ? (
        <div className="text-center text-red-600 p-4">
          Error loading ratings data
        </div>
      ) : lessonRatings && lessonRatings.length > 0 ? (
        <div className="mt-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Rated Lessons */}
            <div className="bg-gray-100 rounded-lg p-4 shadow-sm">
              <h5 className="m-0 mb-3 text-gray-700 text-base font-semibold">
                Top Rated Lessons
              </h5>
              <div className="space-y-2">
                {sortedRatings.slice(0, 5).map((rating) => (
                  <div
                    key={rating.lessonId}
                    className="flex text-sm justify-between py-2 border-b border-gray-200 last:border-b-0"
                  >
                    <span className="flex-1 mr-3 whitespace-nowrap overflow-hidden text-ellipsis">
                      {getLessonTitle(rating.lessonId)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        ({rating.count})
                      </span>
                      <span className="text-gray-600 font-bold">
                        {rating.average.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lowest Rated Lessons */}
            <div className="bg-gray-100 rounded-lg p-4 shadow-sm">
              <h5 className="m-0 mb-3 text-gray-700 text-base font-semibold">
                Lowest Rated Lessons
              </h5>
              <div className="space-y-2">
                {lowestRatings.slice(0, 5).map((rating) => (
                  <div
                    key={rating.lessonId}
                    className="flex text-sm justify-between py-2 border-b border-gray-200 last:border-b-0"
                  >
                    <span className="flex-1 mr-3 whitespace-nowrap overflow-hidden text-ellipsis">
                      {getLessonTitle(rating.lessonId)}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        ({rating.count})
                      </span>
                      <span className="text-gray-600 font-bold">
                        {rating.average.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {ratingChartMeta && aggregateRatingRow && (
            <div className="space-y-3">
              <h4 className="m-0 text-gray-700 text-base font-semibold">
                Average ratings across all lessons
              </h4>
              <p className="m-0 text-sm text-gray-600">
                Uses the same topic, segment, and date filters as above.
                Averages are weighted by number of responses.
              </p>
              <div className="w-full max-w-full overflow-auto">
                <HorizontalBarChart
                  data={[aggregateRatingRow]}
                  title="Average across lessons"
                  xAxisLabel="Average Rating (1-5 Scale)"
                  datasets={ratingChartMeta.chartDatasets}
                  maxScale={5}
                  disableSorting={true}
                  maxLabelLines={3}
                  tooltipText="Averages pool all lesson feedback in the current filters; each bar is weighted by how many people answered that question."
                />
              </div>
            </div>
          )}

          {/* Feedback Breakdown Charts */}
          {sortedRatings.length > 0 &&
            ratingChartMeta &&
            (() => {
              const { allCategoriesWithOverall, chartDatasets } =
                ratingChartMeta;

              // Prepare data for grouped bar chart with sorting
              // Include all lessons, not just those with subfeedback
              let lessonsToDisplay = sortedRatings;

              // Apply sorting based on selected rating type and sort order
              lessonsToDisplay = [...lessonsToDisplay].sort((a, b) => {
                let aValue: number;
                let bValue: number;

                if (selectedRatingType === "Overall Rating") {
                  aValue = a.average;
                  bValue = b.average;
                } else {
                  aValue = a.subFeedbackAverages?.[selectedRatingType] || 0;
                  bValue = b.subFeedbackAverages?.[selectedRatingType] || 0;
                }

                // Apply sort order
                if (sortOrder === "highest") {
                  return bValue - aValue;
                } else if (sortOrder === "lowest") {
                  return aValue - bValue;
                } else if (sortOrder === "most_rated") {
                  return b.count - a.count;
                } else {
                  // least_rated
                  return a.count - b.count;
                }
              });

              // Transform data for HorizontalBarChart component
              const chartDataForHorizontal = lessonsToDisplay.map((r) => {
                const dataPoint: {
                  _id: string;
                  title: string;
                  topic?: string;
                  count?: number;
                  overallCount?: number;
                  [key: string]: string | number | undefined;
                } = {
                  _id: r.lessonId,
                  title: getLessonTitle(r.lessonId),
                  topic: getTopicTitleForLesson(r.lessonId), // Topic for multi-line label
                  count: r.count,
                  overallCount: r.count, // Store overall count
                  "Overall Rating": r.average,
                };

                // Add all subfeedback categories with their individual counts
                allCategoriesWithOverall.forEach((category) => {
                  if (category !== "Overall Rating") {
                    dataPoint[category] =
                      r.subFeedbackAverages?.[category] || 0;
                    // Store the count for each category to display in tooltip
                    dataPoint[`${category}_count`] =
                      r.subFeedbackCounts?.[category] || 0;
                  }
                });

                return dataPoint;
              });

              return (
                <div className="space-y-4">
                  {/* Graph Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sort Order
                      </label>
                      <Combobox
                        options={[
                          { value: "highest", label: "Highest to Lowest" },
                          { value: "lowest", label: "Lowest to Highest" },
                          { value: "most_rated", label: "Most Rated First" },
                          { value: "least_rated", label: "Least Rated First" },
                        ]}
                        onChange={(value) => setSortOrder(value.value)}
                        value={{
                          value: sortOrder,
                          label:
                            sortOrder === "highest"
                              ? "Highest to Lowest"
                              : sortOrder === "lowest"
                                ? "Lowest to Highest"
                                : sortOrder === "most_rated"
                                  ? "Most Rated First"
                                  : "Least Rated First",
                        }}
                        placeholder="Sort Order"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rating Type
                      </label>
                      <Combobox
                        options={availableRatingTypes.map((type) => ({
                          value: type,
                          label: type,
                        }))}
                        onChange={(value) => setSelectedRatingType(value.value)}
                        value={{
                          value: selectedRatingType,
                          label: selectedRatingType,
                        }}
                        placeholder="Rating Type"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        variant={hasActiveGraphFilters ? "dark" : "secondary"}
                        size="md"
                        onClick={() => {
                          setSortOrder("highest");
                          setSelectedRatingType("Overall Rating");
                        }}
                        className="w-full"
                        disabled={!hasActiveGraphFilters}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="w-full max-w-full overflow-auto">
                    <HorizontalBarChart
                      key={`feedback-chart-${selectedRatingType}`}
                      data={chartDataForHorizontal}
                      title={
                        selectedRatingType === "Overall Rating"
                          ? "Detailed Feedback Component Ratings by Lesson"
                          : `${selectedRatingType} Ratings by Lesson`
                      }
                      xAxisLabel="Average Rating (1-5 Scale)"
                      datasets={chartDatasets}
                      maxScale={5}
                      selectedDataset={
                        selectedRatingType === "Overall Rating"
                          ? undefined
                          : selectedRatingType
                      }
                      disableSorting={true}
                      maxLabelLines={3}
                      onBarClick={(itemId) => {
                        // Toggle selection: if clicking the same lesson, deselect it
                        if (selectedLessonId === itemId) {
                          setSelectedLessonId("");
                          if (onLessonSelect) {
                            onLessonSelect("", "");
                          }
                        } else {
                          setSelectedLessonId(itemId);
                          const clickedTopicId = getTopicIdForLesson(itemId);
                          if (clickedTopicId && onLessonSelect) {
                            onLessonSelect(itemId, clickedTopicId);
                          }
                        }
                      }}
                      selectedItemId={selectedLessonId}
                      tooltipText="Click on legend items to show/hide them. Click on any bar to filter lesson-specific feedback below."
                    />
                  </div>
                </div>
              );
            })()}
        </div>
      ) : (
        <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-lg">
          <p className="text-lg mb-2">No ratings available</p>
          <p className="text-sm">
            {topicId
              ? "No lessons in this topic have been rated yet."
              : "No lessons have been rated yet."}
          </p>
        </div>
      )}
    </Card>
  );
};

export default FeedbackAnalyticsWidget;
